import React, { createElement, memo, ReactElement, ReactNode } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui-compat';
import { InfoIcon } from '../../assets/icons';
import { CSSClasses } from '@e2m-customer-portal/shared';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '4px !important',
  },

  heading: {
    fontWeight: 700,
    padding: 0,
  },

  tooltipIcon: {
    fontSize: '1rem',
    marginLeft: theme.spacing(1),
  },
}));

export interface HeadingProps {
  /**
   * A component to use as the wrapper for the heading text.
   * @default Typography
   */
  component?: React.ElementType;

  /**
   * An optional class name to be applied to this component.
   */
  className?: string;

  /**
   * Optional class names to be applied to specific parts of this component.
   */
  classes?: CSSClasses<'root' | 'heading'>;

  /**
   * An optional tooltip to display besides the label.
   */
  tooltip?: string;

  /**
   * The label for this heading.
   */
  children?: ReactNode;
}

function WrappedHeading({
  component,
  className,
  tooltip,
  children,
  classes: propsClasses,
}: Readonly<HeadingProps>): ReactElement<HeadingProps> {
  const { classes, cx } = useStyles();

  return (
    <div className={cx(classes.root, propsClasses?.root, className)}>
      {createElement(component ?? Typography, { className: propsClasses?.heading ?? classes.heading }, children)}

      {tooltip && (
        <Tooltip title={tooltip} placement="top" enterTouchDelay={100} leaveDelay={100}>
          <InfoIcon color="primary" className={classes.tooltipIcon} />
        </Tooltip>
      )}
    </div>
  );
}

export const Heading = memo(WrappedHeading);
